import React, { useState, useEffect, useCallback, useRef } from 'react';
import { departementCourant, peutModifier } from '../services/droits';
import { stageAPI } from '../services/api';
import PhotoEtudiant from './PhotoEtudiant';
import { teinteAvatar, initiales } from '../services/etudiantsAffichage';
import StageModale from './StageModale';
import StageBlocRepliable from './StageBlocRepliable';
import ImportModal, { NOM_COLLAGE } from './ImportModal';
import { versDate } from '../services/horodatage';

/**
 * SUIVI DE STAGE — le registre Alternance (lot 3.3 bis)
 * =====================================================
 * Spec : doc/modules/suivi_de_stage.md § 3bis, écarts E70 → E77
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html, #tab-alternance
 *
 * ── QUI APPARAÎT (E70) ─────────────────────────────────────────────────────
 * Les inscrits de l'année en régime `apprentis` ou `fc`, quelle que soit leur
 * formation. Personne ne s'ajoute ici : la population vient de l'import des
 * ÉTUDIANTS. D'où l'absence du bouton « Ajouter un apprenti » de la maquette
 * (E72) — tous les apprentis sont déjà dans la liste.
 *
 * ── UNE SEULE ACTION : « MODIFIER » (E72) ──────────────────────────────────
 * L'import est le point d'entrée dans 100 % des cas (E74) ; « Modifier » corrige.
 * L'enveloppe de la maquette est retirée : aucun modèle de courriel derrière.
 *
 * ── « REPRENDRE DE L'ANNÉE PRÉCÉDENTE » (lot 3.5b-0, E151) ─────────────────
 * Un apprenti garde le plus souvent sa société, son maître et son tuteur d'une
 * année sur l'autre (passage ou redoublement). La reprise ne remplit QUE le
 * vide ; un import ultérieur reste prioritaire (pastille « Repris »).
 *
 * ── L'HISTORIQUE N'APPARAÎT QU'APRÈS UNE VRAIE MODIFICATION (E77) ──────────
 * Tranché par Bertrand : « dans les faits il y a très très peu de changements ».
 * Un bloc qui dirait « rempli par import » sous chaque formulaire serait du bruit.
 */

const DELAI_RECHERCHE_MS = 300;
const FILTRES_VIDES = { regime: '', sousNiveauId: '', recherche: '' };

/** La clé de mémoire de l'import : PAR COMPTE (convention E29, écart E75). */
const cleMemoireImport = (user) => `import.correspondance.stage-alternance.${user?.id || 'anonyme'}`;

/**
 * Les champs importables. Les trois premiers servent à RATTACHER une ligne à un
 * inscrit (écart E80) : le numéro, OU le nom et le prénom, OU une colonne « Nom
 * et prénom » d'un seul tenant. Les autres viennent du serveur (`champs`),
 * écrits une seule fois là-bas.
 */
function champsImport(champs) {
    return [
        { key: 'code_etudiant',        label: 'Numéro étudiant',                 required: false },
        { key: 'etudiant_nom',         label: "Nom de l'étudiant",               required: false },
        { key: 'etudiant_prenom',      label: "Prénom de l'étudiant",            required: false },
        { key: 'etudiant_nom_complet', label: "Nom et prénom de l'étudiant (une colonne)", required: false },
        ...(champs || []).map((c) => ({ key: c.cle, label: c.libelle, required: false }))
    ];
}

/**
 * Ce qui ne s'importe PAS (Bertrand, 2026-10-14) : le TUTEUR PÉDAGOGIQUE s'affecte dans l'application, jamais par un
 * fichier — une affectation pose une date, l'échéance de la visite, le lien de la fiche (invalidé au changement), le
 * repère « changé » de la transmission au CFA et l'historique. L'import le DIT au lieu de laisser croire à un oubli.
 */
const NON_IMPORTABLES = [{
    libelle: 'Tuteur pédagogique',
    raisonCourte: "s'affecte dans l'onglet Tuteurs",
    message: "ne s'importe pas : il s'affecte à la main dans l'onglet Tuteurs (affectation à l'unité ou aide à la répartition). Cette colonne sera ignorée.",
    motif: /tuteur/i
}];

/** « Au moins un moyen de rattacher la ligne » — ce que `required` ne sait pas dire. */
function validerRattachement(cles) {
    const a = (k) => cles.includes(k);
    if (a('code_etudiant') || a('etudiant_nom_complet') || (a('etudiant_nom') && a('etudiant_prenom'))) return null;
    if (a('etudiant_nom') || a('etudiant_prenom')) {
        return "Associez le nom ET le prénom de l'étudiant — ou le numéro étudiant.";
    }
    return "Associez au moins le numéro étudiant, ou le nom et le prénom de l'étudiant : c'est ce qui rattache chaque ligne à un inscrit.";
}

/** La pastille Source, avec le vocabulaire de la maquette. */
function PastilleSource({ etat, anneePrecedente }) {
    if (etat === 'a_completer') {
        return <span className="badge" style={{ background: '#fee2e2', color: '#991b1b' }}>À compléter</span>;
    }
    if (etat === 'reprise') {
        // Lot 3.5b-0 (E151) : ni un import, ni une correction — un import ultérieur reste prioritaire.
        return (
            <span className="badge" style={{ background: '#ede9fe', color: '#6d28d9' }}
                  title="Repris de la ligne de l'année précédente : un import l'actualisera sans vous le signaler comme une correction">
                ↻ Repris{anneePrecedente ? ` de ${anneePrecedente}` : ''}
            </span>
        );
    }
    if (etat === 'manuel') {
        return (
            <span className="badge" style={{ background: '#fef3c7', color: '#b45309' }}
                  title="Corrigé avec « Modifier » après l'import : un nouvel import l'écraserait, il vous le signalera">
                ✎ Corrigé à la main
            </span>
        );
    }
    return <span className="badge badge-ok">✅ Importé</span>;
}

const dateCourte = (iso) => (versDate(iso)
    ? versDate(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—');
const dateHeure = (iso) => (versDate(iso)
    ? versDate(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—');

/**
 * La modale « Modifier ».
 * ⚠️ Deux temps (règle 6) : le formulaire, puis ce qui s'est passé.
 */
function ModaleModifier({ etudiantId, departementId, anneeId, champs, modifiable, onFermer, onEnregistre }) {
    const [ligne, setLigne] = useState(null);
    const [valeurs, setValeurs] = useState({});
    const [erreur, setErreur] = useState(null);
    const [enCours, setEnCours] = useState(false);
    const [resultat, setResultat] = useState(null);

    useEffect(() => {
        let vivant = true;
        stageAPI.getLigneAlternance(etudiantId, departementId, anneeId)
            .then((r) => {
                if (!vivant) return;
                setLigne(r.data.ligne);
                setValeurs(Object.fromEntries((champs || []).map((c) => [c.cle, r.data.ligne[c.cle] || ''])));
            })
            .catch((e) => vivant && setErreur(e.response?.data?.error || e.message));
        return () => { vivant = false; };
    }, [etudiantId, departementId, anneeId, champs]);

    const enregistrer = async () => {
        setEnCours(true); setErreur(null);
        try {
            const r = await stageAPI.modifierLigneAlternance(etudiantId, departementId, anneeId, valeurs);
            setResultat(r.data);
            if (!r.data.inchange) onEnregistre();
        } catch (e) {
            setErreur(e.response?.data?.error || e.message);
        } finally {
            setEnCours(false);
        }
    };

    const nom = ligne ? `${ligne.nom} ${ligne.prenom}` : '…';

    if (resultat) {
        return (
            <StageModale titre={`Coordonnées — ${nom}`} icone="fa-circle-check" ton="succes" onFermer={onFermer}>
                {resultat.inchange
                    ? <p>Aucune valeur n'a changé : rien n'a été enregistré, et l'historique n'a pas bougé.</p>
                    : <p>{resultat.changements} champ{resultat.changements > 1 ? 's' : ''} modifié{resultat.changements > 1 ? 's' : ''}.
                        La ligne passe en <strong>« Corrigé à la main »</strong>, et la modification est inscrite dans son historique.</p>}
            </StageModale>
        );
    }

    const blocs = [
        { titre: 'Entreprise', icone: 'fa-building', cles: ['entreprise_nom', 'entreprise_adresse', 'entreprise_cp', 'entreprise_ville'] },
        { titre: "Maître d'apprentissage", icone: 'fa-user-tie', cles: ['maitre_civilite', 'maitre_nom', 'maitre_prenom', 'maitre_fonction', 'maitre_email', 'maitre_telephone'] }
    ];
    const libelle = Object.fromEntries((champs || []).map((c) => [c.cle, c]));
    const historique = ligne?.historique || [];
    const nbModifs = historique.filter((h) => h.changements.some((c) => c.avant !== null)).length;

    return (
        <StageModale
            titre={`Modifier — ${nom}`}
            icone="fa-pen"
            classe="stg-modale-large"
            enCours={enCours}
            libelleValider="Enregistrer"
            onValider={ligne && modifiable ? enregistrer : undefined}
            onFermer={onFermer}
        >
            {!ligne && !erreur && <p>Lecture…</p>}
            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}
            {ligne && (
                <>
                    <div className="stg-alt-sous-titre">
                        {ligne.formation_nom || '—'} · {ligne.regime === 'fc' ? 'Formation continue' : 'Apprentissage'}
                        {ligne.code_etudiant ? ` · n° ${ligne.code_etudiant}` : ''}
                    </div>
                    {!modifiable && (
                        <div className="stg-encart stg-encart-alerte">
                            <i className="fa-solid fa-lock" />
                            <span>Lecture seule : votre profil ne permet pas de modifier ce module.</span>
                        </div>
                    )}
                    {blocs.map((b) => (
                        <fieldset key={b.titre} className="stg-alt-bloc">
                            <legend><i className={'fa-solid ' + b.icone} /> {b.titre}</legend>
                            <div className="stg-alt-grille">
                                {b.cles.map((cle) => (
                                    <label key={cle} className={cle === 'entreprise_adresse' || cle === 'entreprise_nom' ? 'stg-alt-large' : ''}>
                                        <span>{libelle[cle]?.libelle || cle}</span>
                                        <input
                                            className="input-sm"
                                            value={valeurs[cle] || ''}
                                            maxLength={libelle[cle]?.max}
                                            disabled={!modifiable || enCours}
                                            onChange={(e) => setValeurs((v) => ({ ...v, [cle]: e.target.value }))}
                                        />
                                    </label>
                                ))}
                            </div>
                        </fieldset>
                    ))}

                    {/* ⚠️ E77 : le bloc n'existe qu'après une VRAIE modification. */}
                    {ligne.a_ete_modifie && (
                        <div className="stg-alt-historique">
                            <StageBlocRepliable
                                key={etudiantId}
                                resume={<>
                                    <i className="fa-solid fa-clock-rotate-left" /> Historique —{' '}
                                    {nbModifs} modification{nbModifs > 1 ? 's' : ''}, dernière le {dateCourte(historique[0]?.date)}
                                    {historique[0]?.acteur ? ` par ${historique[0].acteur}` : ''}
                                </>}
                            >
                                <ul className="stg-alt-histo-liste">
                                    {historique.map((h, i) => (
                                        <li key={i}>
                                            <div className="stg-alt-histo-tete">
                                                <strong>{dateHeure(h.date)}</strong>
                                                {' · '}{h.acteur || <em>—</em>}
                                                {' · '}{h.origine === 'import'
                                                    ? <>import{h.fichier === NOM_COLLAGE ? ' par copier-coller' : h.fichier ? <> de <em>{h.fichier}</em></> : ''}</>
                                                    : h.origine === 'reprise'
                                                        ? <>reprise de <em>{h.fichier || "l'année précédente"}</em></>
                                                        : 'modification manuelle'}
                                            </div>
                                            {h.changements.map((c, j) => (
                                                <div key={j} className="stg-alt-histo-champ">
                                                    <span>{c.libelle}</span>
                                                    <span className={'avant' + (c.avant === null ? ' vide' : '')}>{c.avant ?? '—'}</span>
                                                    <i className="fa-solid fa-arrow-right" />
                                                    <span className="apres">{c.apres ?? '—'}</span>
                                                </div>
                                            ))}
                                        </li>
                                    ))}
                                </ul>
                            </StageBlocRepliable>
                        </div>
                    )}
                </>
            )}
        </StageModale>
    );
}

/** Les raisons pour lesquelles le tuteur de l'an dernier n'est pas repris (E151). */
const TUTEUR_NON_REPRIS = {
    absent: "n'est pas dans les enseignants actifs de l'année",
    homonymes: "plusieurs enseignants de l'année portent ce nom : choisissez-le dans l'onglet Tuteurs",
    deja: "un tuteur est déjà affecté cette année"
};

/**
 * Lot 3.5b-0 (E151) — « Reprendre de l'année précédente ».
 * ⚠️ Deux temps (règle 6) : la relecture à cocher, puis le bilan de ce qui a été repris.
 * Ne remplit que le vide : le serveur recalcule tout au moment de reprendre.
 */
function ModaleReprise({ departementId, anneeId, onFermer, onRepris }) {
    const [donnees, setDonnees] = useState(null);
    const [coches, setCoches] = useState(new Set());
    const [avecTuteur, setAvecTuteur] = useState(true);
    const [erreur, setErreur] = useState(null);
    const [enCours, setEnCours] = useState(false);
    const [bilan, setBilan] = useState(null);

    useEffect(() => {
        let vivant = true;
        stageAPI.getRepriseAlternance(departementId, anneeId)
            .then((r) => {
                if (!vivant) return;
                setDonnees(r.data);
                setCoches(new Set((r.data.lignes || []).map((l) => l.etudiant_id)));
            })
            .catch((e) => vivant && setErreur(e.response?.data?.error || e.message));
        return () => { vivant = false; };
    }, [departementId, anneeId]);

    const lignes = donnees?.lignes || [];
    const precedente = donnees?.annee_precedente?.libelle || "l'année précédente";
    const basculer = (id) => setCoches((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    // Une ligne qui n'apporte qu'un tuteur n'a plus rien à reprendre si le tuteur est décoché.
    const utiles = lignes.filter((l) => coches.has(l.etudiant_id) && (l.coordonnees || (avecTuteur && l.tuteur)));

    const reprendre = async () => {
        setEnCours(true); setErreur(null);
        try {
            const r = await stageAPI.reprendreAlternance(departementId, anneeId, utiles.map((l) => l.etudiant_id), avecTuteur);
            setBilan(r.data);
            onRepris();
        } catch (e) {
            setErreur(e.response?.data?.error || e.message);
        } finally {
            setEnCours(false);
        }
    };

    if (bilan) {
        return (
            <StageModale titre="Reprise de l'année précédente" icone="fa-circle-check" ton="succes" onFermer={onFermer}>
                <p>
                    <strong>{bilan.coordonnees}</strong> apprenti{bilan.coordonnees > 1 ? 's' : ''} repris avec
                    l'entreprise et le maître d'apprentissage de {bilan.annee_precedente?.libelle || precedente}
                    {' · '}<strong>{bilan.tuteurs}</strong> tuteur{bilan.tuteurs > 1 ? 's' : ''} pédagogique{bilan.tuteurs > 1 ? 's' : ''} affecté{bilan.tuteurs > 1 ? 's' : ''}.
                </p>
                <p className="stg-alt-sous">Chaque ligne porte « Repris de {bilan.annee_precedente?.libelle || precedente} » dans son historique. Aucun courriel n'est parti.</p>
                {bilan.ignores?.length > 0 && (
                    <div className="stg-encart stg-encart-alerte">
                        <i className="fa-solid fa-triangle-exclamation" />
                        <span>
                            Non repris, déjà rempli entre-temps : {bilan.ignores.map((x) => x.nom || `n° ${x.etudiant_id}`).join(', ')}.
                        </span>
                    </div>
                )}
            </StageModale>
        );
    }

    return (
        <StageModale
            titre={`Reprendre de ${precedente}`}
            icone="fa-clock-rotate-left"
            classe="stg-modale-large stg-modale-reprise"
            enCours={enCours}
            libelleValider={`Reprendre ${utiles.length} apprenti${utiles.length > 1 ? 's' : ''}`}
            onValider={utiles.length ? reprendre : undefined}
            onFermer={onFermer}
        >
            {!donnees && !erreur && <p>Lecture…</p>}
            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}
            {donnees && (
                <>
                    <p style={{ marginTop: 0 }}>
                        Ces apprentis avaient une ligne en <strong>{precedente}</strong>. Leur entreprise, leur maître
                        d'apprentissage et leur tuteur pédagogique peuvent être repris, qu'ils passent à l'année suivante
                        ou redoublent. <strong>Seul ce qui est vide est rempli</strong> : rien de ce qui est déjà renseigné
                        cette année n'est écrasé. Décochez un apprenti qui a changé d'entreprise.
                    </p>
                    <label className="stg-reprise-option">
                        <input type="checkbox" checked={avecTuteur} disabled={enCours} onChange={(e) => setAvecTuteur(e.target.checked)} />
                        {' '}Reprendre aussi le tuteur pédagogique <span className="stg-alt-sous">(aucun courriel n'est envoyé)</span>
                    </label>
                    <div className="stg-reprise-tete">
                        <label>
                            <input type="checkbox" disabled={enCours}
                                   checked={coches.size === lignes.length && lignes.length > 0}
                                   onChange={(e) => setCoches(e.target.checked ? new Set(lignes.map((l) => l.etudiant_id)) : new Set())} />
                            {' '}{coches.size} / {lignes.length} coché{coches.size > 1 ? 's' : ''}
                        </label>
                    </div>
                    <div className="stg-reprise-liste">
                        {lignes.map((l) => {
                            const a = l.avant || {};
                            const maitre = [a.maitre_civilite, a.maitre_prenom, a.maitre_nom].filter(Boolean).join(' ');
                            const coche = coches.has(l.etudiant_id);
                            return (
                                <label key={l.etudiant_id} className={'stg-tv-ligne alt stg-reprise-ligne' + (coche ? '' : ' decoche')}>
                                    <input type="checkbox" checked={coche} disabled={enCours} onChange={() => basculer(l.etudiant_id)} />
                                    <PhotoEtudiant
                                        chemin={l.photo_url}
                                        className="stg-avatar stg-avatar-photo"
                                        alt=""
                                        secours={<div className="stg-avatar" style={{ background: teinteAvatar(l.nom, l.prenom) }}>{initiales(l.nom, l.prenom)}</div>}
                                    />
                                    <div className="stg-reprise-qui">
                                        <div><strong>{l.nom}</strong> {l.prenom}</div>
                                        <div className="stg-alt-sous">
                                            {l.formation_avant || '—'} <i className="fa-solid fa-arrow-right" /> <strong>{l.formation || '—'}</strong>
                                            {l.redouble && <span className="badge" style={{ marginLeft: 6, background: '#fef3c7', color: '#b45309' }}>redouble</span>}
                                        </div>
                                    </div>
                                    <div className="stg-reprise-ent">
                                        {l.coordonnees ? (<>
                                            <div><i className="fa-solid fa-building" /> <strong>{a.entreprise_nom || '—'}</strong>
                                                {(a.entreprise_ville || a.entreprise_cp) && <span className="stg-alt-sous"> · {[a.entreprise_cp, a.entreprise_ville].filter(Boolean).join(' ')}</span>}</div>
                                            <div className="stg-alt-sous"><i className="fa-solid fa-user-tie" /> {maitre || '—'}</div>
                                        </>) : (
                                            <div className="stg-alt-sous"><i className="fa-solid fa-check" /> Entreprise et maître déjà renseignés cette année</div>
                                        )}
                                    </div>
                                    <div className="stg-tv-etat">
                                        {l.tuteur ? (
                                            <span className={avecTuteur ? 'stg-reprise-ok' : 'stg-alt-sous'}>
                                                <i className="fa-solid fa-chalkboard-user" /> {l.tuteur.nom} {l.tuteur.prenom}
                                                {!avecTuteur && ' (non repris)'}
                                            </span>
                                        ) : l.tuteur_avant ? (
                                            <span className="stg-reprise-non" title={TUTEUR_NON_REPRIS[l.tuteur_motif] || ''}>
                                                <i className="fa-solid fa-triangle-exclamation" /> {l.tuteur_avant} {TUTEUR_NON_REPRIS[l.tuteur_motif] || 'non repris'}
                                            </span>
                                        ) : <span className="stg-alt-sous">Pas de tuteur l'an dernier</span>}
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                </>
            )}
        </StageModale>
    );
}

function StageAlternance({ config, user, anneeId: anneeModule, rechercheInitiale = '' }) {
    const departementId = departementCourant(user, config);
    const modifiable = peutModifier(user, departementId, 'stage');
    const raisonLectureSeule = 'Lecture seule : votre profil ne permet pas de modifier ce module.';

    const [anneeId, setAnneeId] = useState(anneeModule || null);
    const [donnees, setDonnees] = useState(null);
    const [chargement, setChargement] = useState(true);
    const [erreur, setErreur] = useState(null);
    // Lot C.1 (CB3) : ouvert depuis la Certification, la recherche arrive PRÉ-REMPLIE.
    const [filtres, setFiltres] = useState(() => ({ ...FILTRES_VIDES, recherche: rechercheInitiale }));
    const [recherche, setRecherche] = useState(rechercheInitiale);
    const [ouverte, setOuverte] = useState(null);
    const [importOuvert, setImportOuvert] = useState(false);
    const [repriseOuverte, setRepriseOuverte] = useState(false);

    useEffect(() => { if (anneeModule) setAnneeId(anneeModule); }, [anneeModule]);

    const minuteur = useRef(null);
    useEffect(() => {
        if (minuteur.current) clearTimeout(minuteur.current);
        minuteur.current = setTimeout(() => setFiltres((f) => ({ ...f, recherche })), DELAI_RECHERCHE_MS);
        return () => { if (minuteur.current) clearTimeout(minuteur.current); };
    }, [recherche]);

    // ⚠️ Aucun rappel de parent dans ces dépendances (leçon du lot 1.3 bis).
    const recharger = useCallback(async () => {
        if (!departementId) return;
        setChargement(true); setErreur(null);
        try {
            const r = await stageAPI.getAlternance(departementId, anneeId, filtres);
            setDonnees(r.data);
        } catch (e) {
            setErreur(e.response?.data?.detail || e.response?.data?.error || e.message || 'Lecture du registre impossible');
            setDonnees(null);
        } finally {
            setChargement(false);
        }
    }, [departementId, anneeId, filtres]);

    useEffect(() => { recharger(); }, [recharger]);

    const poser = (cle, valeur) => setFiltres((f) => ({ ...f, [cle]: valeur }));

    if (!departementId) {
        return (
            <div className="stg-encart stg-encart-alerte">
                <i className="fa-solid fa-triangle-exclamation" />
                <span>Aucun département sélectionné : le registre ne peut pas être lu.</span>
            </div>
        );
    }

    const lignes = donnees?.lignes || [];
    const anneeAffichee = donnees?.annee;
    const aCompleter = lignes.filter((l) => l.etat_source === 'a_completer').length;

    return (
        <>
            <div className="stg-alt-info">
                <i className="fa-solid fa-circle-info" />
                <div>
                    L'alternance est gérée hors application : ce volet capte, pour chaque apprenti et chaque inscrit
                    en formation continue, son <strong>entreprise</strong> et son <strong>maître d'apprentissage</strong>,
                    par <strong>import</strong>. C'est la <strong>source unique</strong> que lisent <strong>Planning oraux</strong>{' '}
                    et la <strong>certification des documents</strong>.
                </div>
            </div>

            <div className="stg-filtres">
                <div className="stg-recherche">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input
                        placeholder="Rechercher un apprenti, une entreprise, un maître…"
                        value={recherche}
                        onChange={(e) => setRecherche(e.target.value)}
                        aria-label="Rechercher un apprenti, une entreprise, un maître"
                    />
                </div>
                <select className="input-sm stg-filtre" value={filtres.regime} aria-label="Régime"
                        onChange={(e) => poser('regime', e.target.value)}>
                    <option value="">Tous les régimes</option>
                    {(donnees?.regimes || []).map((r) => <option key={r.code} value={r.code}>{r.libelle}</option>)}
                </select>
                <select className="input-sm stg-filtre" value={filtres.sousNiveauId} aria-label="Formation"
                        onChange={(e) => poser('sousNiveauId', e.target.value)}>
                    <option value="">Toutes les formations</option>
                    {(donnees?.formations || []).map((f) => <option key={f.id} value={f.id}>{f.nom}</option>)}
                </select>
                <select className="input-sm stg-filtre" value={anneeId || anneeAffichee?.id || ''} aria-label="Année"
                        onChange={(e) => setAnneeId(e.target.value ? Number(e.target.value) : null)}>
                    {(donnees?.annees || []).map((a) => <option key={a.id} value={a.id}>{a.libelle}</option>)}
                </select>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    {/* Lot 3.5b-0 (E151) : seulement s'il y a quelque chose à reprendre. */}
                    {donnees?.a_reprendre > 0 && (
                        <button type="button" className="btn btn-outline btn-sm"
                                disabled={!modifiable}
                                title={modifiable
                                    ? `Reprendre l'entreprise, le maître d'apprentissage et le tuteur de ${donnees.annee_precedente?.libelle || "l'année précédente"} — seul le vide est rempli`
                                    : raisonLectureSeule}
                                onClick={() => setRepriseOuverte(true)}>
                            <i className="fa-solid fa-clock-rotate-left" /> Reprendre de {donnees.annee_precedente?.libelle || "l'année précédente"}
                            <span className="stg-reprise-nb">{donnees.a_reprendre}</span>
                        </button>
                    )}
                    <button type="button" className="btn btn-outline btn-sm"
                            disabled={!modifiable}
                            title={modifiable ? 'Importer le fichier du service (.csv, .xls, .xlsx) ou coller son tableau (Ctrl+V)' : raisonLectureSeule}
                            onClick={() => setImportOuvert(true)}>
                        <i className="fa-solid fa-file-import" /> Importer
                    </button>
                </div>
            </div>
            {!modifiable && (
                <div className="stg-alt-raison"><i className="fa-solid fa-lock" /> {raisonLectureSeule}</div>
            )}

            {erreur && (
                <div className="stg-encart stg-encart-erreur">
                    <i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span>
                </div>
            )}

            <div className="card">
                <div className="card-header">
                    <span>
                        <i className="fa-solid fa-table-list" />{' '}
                        {chargement && !donnees
                            ? 'Lecture…'
                            : `${lignes.length} inscrit${lignes.length > 1 ? 's' : ''}`}
                        {anneeAffichee?.libelle ? ` · ${anneeAffichee.libelle}` : ''}
                        {aCompleter > 0 && <span className="stg-alt-compteur"> · {aCompleter} à compléter</span>}
                    </span>
                </div>
                <div className="stg-table-defile">
                    <table>
                        <thead>
                            <tr>
                                <th>Apprenti</th>
                                <th>Entreprise</th>
                                <th>Maître d'apprentissage</th>
                                <th>Contact</th>
                                <th>Formation</th>
                                <th>Tuteur pédagogique</th>
                                <th>Source</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!chargement && lignes.length === 0 && (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: 'center', color: '#64748b', padding: '24px 14px' }}>
                                        {filtres.regime || filtres.sousNiveauId || filtres.recherche
                                            ? 'Aucun inscrit ne correspond à ces filtres.'
                                            : "Aucun inscrit en apprentissage ou en formation continue cette année — ils apparaissent ici dès leur import dans le module Étudiants."}
                                    </td>
                                </tr>
                            )}
                            {lignes.map((l) => {
                                const vide = l.etat_source === 'a_completer';
                                const maitre = [l.maitre_civilite, l.maitre_prenom, l.maitre_nom].filter(Boolean).join(' ');
                                return (
                                    <tr key={l.etudiant_id}>
                                        <td>
                                            <div className="stg-identite">
                                                <PhotoEtudiant
                                                    chemin={l.photo_url}
                                                    className="stg-avatar stg-avatar-photo"
                                                    alt=""
                                                    secours={(
                                                        <div className="stg-avatar" style={{ background: teinteAvatar(l.nom, l.prenom) }}>
                                                            {initiales(l.nom, l.prenom)}
                                                        </div>
                                                    )}
                                                />
                                                <div><strong>{l.nom}</strong> {l.prenom}</div>
                                            </div>
                                        </td>
                                        {vide ? (
                                            <td colSpan={2} className="stg-alt-manque">
                                                <i className="fa-solid fa-triangle-exclamation" /> Entreprise / maître non renseignés — à compléter avant la soutenance
                                            </td>
                                        ) : (<>
                                            <td>
                                                <strong>{l.entreprise_nom}</strong>
                                                {(l.entreprise_ville || l.entreprise_cp) && (
                                                    <div className="stg-alt-sous">{[l.entreprise_ville, l.entreprise_cp && `(${l.entreprise_cp})`].filter(Boolean).join(' ')}</div>
                                                )}
                                            </td>
                                            <td>
                                                {maitre || '—'}
                                                {l.maitre_fonction && <div className="stg-alt-sous">{l.maitre_fonction}</div>}
                                            </td>
                                        </>)}
                                        <td>
                                            {l.maitre_email || l.maitre_telephone ? (<>
                                                {l.maitre_email && <div style={{ fontSize: 11 }}>{l.maitre_email}</div>}
                                                {l.maitre_telephone && <div className="stg-alt-sous">{l.maitre_telephone}</div>}
                                            </>) : '—'}
                                        </td>
                                        <td><span className="badge badge-wait">{l.formation_nom || '—'}</span></td>
                                        <td>{l.tuteur_nom ? `${l.tuteur_prenom || ''} ${l.tuteur_nom}`.trim() : '—'}</td>
                                        <td><PastilleSource etat={l.etat_source} anneePrecedente={donnees?.annee_precedente?.libelle} /></td>
                                        <td className="actions">
                                            {/* « Modifier » s'ouvre aussi en lecture seule : consulter, c'est
                                                voir (E68). Les champs y sont désactivés, et la modale dit pourquoi. */}
                                            <button type="button" className="action-btn"
                                                    title={vide ? 'Compléter' : 'Modifier'}
                                                    onClick={() => setOuverte(l.etudiant_id)}>
                                                <i className="fa-solid fa-pen" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {ouverte && (
                <ModaleModifier
                    key={ouverte}
                    etudiantId={ouverte}
                    departementId={departementId}
                    anneeId={anneeAffichee?.id || anneeId}
                    champs={donnees?.champs}
                    modifiable={modifiable}
                    onFermer={() => setOuverte(null)}
                    onEnregistre={recharger}
                />
            )}

            {repriseOuverte && (
                <ModaleReprise
                    departementId={departementId}
                    anneeId={anneeAffichee?.id || anneeId}
                    onFermer={() => setRepriseOuverte(false)}
                    onRepris={recharger}
                />
            )}

            {importOuvert && (
                <ImportModal
                    titre={`Alternance — ${anneeAffichee?.libelle || ''}`}
                    champs={champsImport(donnees?.champs)}
                    memoireCle={cleMemoireImport(user)}
                    validerCorrespondance={validerRattachement}
                    nonImportables={NON_IMPORTABLES}
                    onPreview={async (rows) => {
                        const r = await stageAPI.apercuImportAlternance(departementId, anneeAffichee?.id || anneeId, rows);
                        return r.data.elements;
                    }}
                    onImport={async (rows, meta) => {
                        const r = await stageAPI.importerAlternance(departementId, anneeAffichee?.id || anneeId, rows, meta?.fichier);
                        return r.data;
                    }}
                    onClose={() => { setImportOuvert(false); recharger(); }}
                />
            )}
        </>
    );
}

export default StageAlternance;
